import * as React from 'react';
import { useNavigate } from "react-router-dom";
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PreviewIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AssignmentIcon from '@mui/icons-material/Assignment';

// Styled MUI menu — same "Customized menus" pattern as the Sales action dropdown
// (estimateActionMenu.tsx) so the Items list matches it exactly.
const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

interface InventoryActionMenuProps {
  row: any;
  onEdit: (row: any) => void;
  onViewBarcodes: (row: any) => void;
  onAudit: (row: any) => void;
}

const InventoryActionMenu: React.FC<InventoryActionMenuProps> = ({ row, onEdit, onViewBarcodes, onAudit }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    navigate(`/inventory/view/${row._id}`);
  };

  const handleEdit = () => {
    handleClose();
    onEdit(row);
  };

  const handleViewBarcodes = () => {
    handleClose();
    onViewBarcodes(row);
  };

  const handleAudit = () => {
    handleClose();
    onAudit(row);
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        style={{ padding: '4px 12px', color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', borderRadius: 10 }}
        id={`inventory-action-button-${row._id}`}
        aria-controls={open ? `inventory-action-menu-${row._id}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        Actions
      </Button>
      <StyledMenu
        id={`inventory-action-menu-${row._id}`}
        MenuListProps={{
          'aria-labelledby': `inventory-action-button-${row._id}`,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleView} disableRipple>
          <PreviewIcon color="primary" />
          View item
        </MenuItem>
        <MenuItem onClick={handleEdit} disableRipple>
          <EditIcon color="primary" />
          Edit item
        </MenuItem>
        {row.barcodeMode && row.barcodeMode !== 'none' && (
          <MenuItem onClick={handleViewBarcodes} disableRipple>
            <QrCode2Icon style={{ color: '#6b46c1' }} />
            Print barcode label
          </MenuItem>
        )}
        <MenuItem onClick={handleAudit} disableRipple>
          <AssignmentIcon style={{ color: '#6b7280' }} />
          Audit Log
        </MenuItem>
      </StyledMenu>
    </>
  );
};

export default InventoryActionMenu;
